<template>
  <VDataGridV2
    :class="{ 'is-buyer': $core.currentPartner.isBuyer }"
    :data="tender.bids"
    entity-type="tender-bids"
    layout="fill"
    :use-filters-cache="false"
    clickable
    :context-menu="$core.currentPartner.isBuyer ? TenderBidsService.contextMenu : null"
    @row:dblclick="showTenderBidDialog"
    @context-menu-item:done="onContextMenuItemDone"
  >
    <VDataGridColumnV2
      id="number"
      width="40px"
      content-align="center"
    >
      <template #default="{ row }">
        {{ tender.bids.indexOf(row) + 1 }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="seller"
      label="Поставщик"
      width="150px"
      content-class="ellipsis"
    >
      <template #default="{ row }">
        <div
          :class="{ 'ellipsis': row.isMine === false }"
          :title="row.isMine ? 'Вы' : row.seller.name"
        >
          {{ row.isMine ? 'Вы' : row.seller.name }}
          <VButton
            v-if="row.isMine && row.mismatchingCharacteristics"
            class="warning"
            theme="link"
            @click="compareWithDemand"
          >
            <div class="attention-dot"/>
            Расхождения
          </VButton>
        </div>

        <VBadges>
          <VBadge
            v-if="tender.isBestBid(row)"
            class="primary"
          >
            Лучшая цена
          </VBadge>

          <VBadge v-if="tender.isAllowSecondBestBid && tender.isSecondBestBid(row)">
            Вторая цена
          </VBadge>

          <template v-if="$core.currentPartner.isBuyer">
            <VBadge
              v-if="row.qtyOrdered.isPositive()"
              class="nowrap"
            >
              Заказано {{ row.qtyOrdered.format(row.unit) }}
            </VBadge>

            <VBadge
              v-if="row.qtyLeft.lessThanOrEqualTo(0)"
              class="warning"
            >
              Исчерпан
            </VBadge>
          </template>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 id="tenderBid" content-class="ellipsis">
      <template #label>
        Ставка
        <div class="description">Тип доставки</div>
      </template>
      <template #default="{ row }">
        <template v-if="row.isMine || $core.currentPartner.isBuyer">
          {{ row.id }}

          <div
            class="description ellipsis"
            :title="row.deliveryType.name"
          >
            {{ row.deliveryType.name }}
          </div>
        </template>
        <template v-else>--</template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isNeedToShowExtraPriceColumns"
      id="price"
      content-align="right"
      header-class="nowrap"
      hint="Цена поставщика с НДС"
      hint-icon-hide
    >
      <!--      hint="Цена поставщика с НДС"-->
      <template #label>
        Цена поставщика с НДС
        <div class="description">В валюте ставки</div>
      </template>
      <template #default="{ row }">
        <template v-if="row.isMine || $core.currentPartner.isBuyer">
          {{ row.priceFullSeller.formatInDisplayCurrency(row.offerCurrencyCode, tender.startAt) }}
          <div
            v-if="row.offerCurrencyCode !== $core.displayedCurrencyCode"
            class="description"
          >
            {{ row.priceFullSeller.format(row.offerCurrencyCode) }}
          </div>
        </template>
        <template v-else>--</template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isNeedToShowExtraPriceColumns"
      id="full-price"
      content-align="right"
      header-class="nowrap"
      hint="Цена с НДС, доставкой, наценкой"
      hint-icon-hide
    >
      <!--      hint="Цена с НДС, доставкой, наценкой"-->
      <template #label>
        Цена покупателя
        <div class="description">В валюте ставки</div>
      </template>
      <template #default="{ row }">
        {{ row.priceFullBuyer.formatInDisplayCurrency(row.offerCurrencyCode, tender.startAt) }}
        <div
          v-if="row.offerCurrencyCode !== $core.displayedCurrencyCode"
          class="description"
        >
          {{ row.priceFullBuyer.format(row.offerCurrencyCode) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="full-price-with-deferred-payment"
      content-align="right"
      width="200px"
      header-class="nowrap"
      hint="Цена с НДС, доставкой, наценкой, отсрочкой"
      hint-icon-hide
    >
      <!--      hint="Цена с НДС, доставкой, наценкой, отсрочкой"-->
      <template #label>
        Цена покупателя с отсрочкой
        <div class="description">В валюте ставки</div>
      </template>
      <template #default="{ row }">
        {{
          row.priceFullBuyerWithDeferredPayment.formatInDisplayCurrency(row.offerCurrencyCode, tender.startAt)
        }}
        <div
          v-if="row.offerCurrencyCode !== $core.displayedCurrencyCode"
          class="description"
        >
          {{ row.priceFullBuyerWithDeferredPayment.format(row.offerCurrencyCode) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner.isSeller"
      id="action"
      width="32px"
    >
      <template #default="{ row }">
        <VButton
          material="open_in_new"
          :disabled="row.isMine === false && $core.currentPartner.isSeller"
          @click="showTenderBidDialog(row)"
        >
        </VButton>
      </template>
    </VDataGridColumnV2>

  </VDataGridV2>
</template>

<script lang="ts">

import TenderBidsService  from '@/domains/TenderBids/TenderBidsService';

import type Tender        from '@/domains/Tenders/Tender';
import type TenderBid     from '@/domains/TenderBids/TenderBid';
import type ContextMenuItem from '@/lib/components/VContextMenu/ContextMenuItem';

import {UnwrapNestedRefs} from "vue";

export default defineComponent({
  name: 'TenderBidsGrid',

  setup() {
    return { TenderBidsService };
  },

  props: {
    tender: {
      type: Object as PropType<Tender | UnwrapNestedRefs<Tender>>,
      required: true,
    },
  },

  /**
   * Одно событие на все изменения ставок: создание, правка и удаление ведут
   * к одному и тому же — карточка перечитывает тендер целиком. Разделять их
   * незачем, слушатель всё равно один.
   */
  emits: ['tenderBid:updated'],

  computed: {
    isNeedToShowExtraPriceColumns(): boolean {
      /** Я так понял что если текущий пользователь второй покупатель, то для них прятать
       * столбцы с ценами */
      return this.$core.currentPartner.isSeller
        || (this.$core.currentPartner.isBuyer && this.$core.currentPartner.id === 67);
    },
  },

  methods: {
    async showTenderBidDialog(tenderBid: TenderBid): void {


      /** В случае если поставщик был одобрен, его удалили из одобренных, в то же время он уже успел сделать ставку,
       * то карточку не дать ему открыть и отредактировать что-нибудь */
      if (
        this.$core.currentPartner.isSeller
        && this.tender.hasApprovedAndNotExcludedSupplyOfferByCurrentPartner === false
        && this.tender.isEveryoneCanBid === false
      ) return;

      // если она не моя, то не открыть
      if (this.$core.currentPartner.isSeller && tenderBid.isMine === false) return;

      const isNeedToUpdate = await this.$core.dialogs.TenderBidCard.showAsPromise(
        { tenderBid });
      if (isNeedToUpdate) this.$emit('tenderBid:updated')
    },

    /**
     * Пункт контекстного меню отработал. Грид эмитит событие по любому пункту,
     * включая просмотровые, запрашивать тендер имеет смысл только после пунктов с refreshAfter.
     *
     * Собственный refresh() грида тут бесполезен — данные приходят пропом :tender
     * из живого тендера
     */
    onContextMenuItemDone(item: ContextMenuItem): void {
      if (item.refreshAfter === true) {
        this.$emit('tenderBid:updated');
      }
    },

    compareWithDemand(): void {
      this.$core.dialogs.OrderOptionsComparisonDialog.show({
        tenderId: this.tender.id,
        showOnlyMineOrderOptionAsSeller: true,
      });
    },
  },
});
</script>
